import { AlertCircle, Mail, RefreshCw } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppLogo from "../../components/ui/AppLogo";
import SplitScreenLayout from "../../components/views/layouts/SplitScreenLayout";
import { useToast } from "../../hooks/useToast";
import { useResendOtpMutation, useVerifyOtpMutation } from "./companyApiSlice";

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  hasError: boolean;
  disabled: boolean;
}

const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  onKeyDown,
  onFocus,
  onPaste,
  hasError,
  disabled,
}) => {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onPaste={onPaste}
      disabled={disabled}
      maxLength={1}
      className={`
        w-10 h-12 sm:w-14 sm:h-14 text-center text-xl font-semibold rounded-lg border-2
        transition-all duration-200 outline-none
        ${
          hasError
            ? "border-red-500 bg-red-50 text-red-600"
            : disabled
            ? "border-gray-200 bg-gray-50 text-gray-400"
            : "border-gray-300 bg-white text-gray-900 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
        }
      `}
      autoComplete="one-time-code"
    />
  );
};

const VerifyEmailWithOtp: React.FC = () => {
  const location = useLocation();
  const email = location.state?.email || "";

  const navigate = useNavigate();
  const toast = useToast();

  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [error, setError] = useState<string>("");
  const [resendCooldown, setResendCooldown] = useState<number>(0);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [verifyOtp, { isLoading }] = useVerifyOtpMutation();
  const [resendOtp, { isLoading: isResendLoading }] = useResendOtpMutation();

  const handleVerifyOtp = async (otpValue: string) => {
    try {
      await verifyOtp({
        email,
        otp: otpValue,
      }).unwrap();

      toast.success({
        title: "OTP Verified!",
        message: "You can now sign in to your account.",
      });

      navigate("/sign-in");
    } catch (err: any) {
      setError(err?.data?.message || "Invalid OTP. Please try again.");
      setOtp(Array(6).fill(""));
      inputRefs.current[0]?.focus();
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;

    try {
      await resendOtp({ email }).unwrap();
      toast.success({
        title: "OTP Resent!",
        message: "Please check your email for the new OTP.",
      });
      setResendCooldown(30);
      setError("");
      setOtp(Array(6).fill(""));
      inputRefs.current[0]?.focus();
    } catch (err: any) {
      setError(err?.data?.message || "Failed to resend OTP. Please try again.");
    }
  };

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleInputChange = useCallback(
    (index: number, value: string) => {
      // Only allow alphanumeric characters
      if (!/^[a-zA-Z0-9]*$/.test(value)) return;

      const newOtp = [...otp];
      newOtp[index] = value.toUpperCase();
      setOtp(newOtp);
      setError("");

      // Move to next input if value is entered
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }

      // Auto-verify if all fields are filled
      if (
        newOtp.every((digit) => digit !== "") &&
        newOtp.join("").length === 6
      ) {
        handleVerifyOtp(newOtp.join(""));
      }
    },
    [otp]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace") {
        e.preventDefault();
        const newOtp = [...otp];

        if (newOtp[index]) {
          newOtp[index] = "";
          setOtp(newOtp);
        } else if (index > 0) {
          newOtp[index - 1] = "";
          setOtp(newOtp);
          inputRefs.current[index - 1]?.focus();
        }
      } else if (e.key === "ArrowLeft" && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else if (e.key === "ArrowRight" && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [otp]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasteData = e.clipboardData
        .getData("text")
        .replace(/\s/g, "")
        .toUpperCase();

      if (!/^[A-Z0-9]{6}$/.test(pasteData)) {
        setError(
          "Please paste a valid 6-character OTP (letters and numbers only)"
        );
        return;
      }

      const newOtp = pasteData.split("").slice(0, 6);
      setOtp([...newOtp, ...Array(6 - newOtp.length).fill("")]);
      setError("");

      // Focus last filled input
      const lastIndex = Math.min(newOtp.length - 1, 5);
      inputRefs.current[lastIndex]?.focus();

      // Auto-verify if complete OTP is pasted
      if (newOtp.length === 6) {
        handleVerifyOtp(newOtp.join(""));
      }
    },
    []
  );

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const otpValue = otp.join("");

    if (otpValue.length !== 6) {
      setError("Please enter the complete 6-character OTP");
      return;
    }

    handleVerifyOtp(otpValue);
  };

  const isOtpComplete = otp.every((digit) => digit !== "");
  const hasError = error !== "";

  return (
    <SplitScreenLayout>
      {/* Right Column - verify otp */}
      <div className="flex items-center justify-center">
        <div className="w-full lg:w-[40vw] mx-auto">
          <div className="flex items-center justify-center">
            <div className="w-full max-w-md mx-auto px-6">
              {/* Logo */}
              <div className="text-center mb-8">
                <div className="flex items-center justify-center">
                  <AppLogo />
                </div>
                <h1 className="text-xl font-bold text-gray-900">
                  Verify Your Email
                </h1>
                <p className="text-gray-600 mt-2">
                  We've sent a verification code to
                </p>
                <p className="text-primary font-medium">{email}</p>
              </div>

              {/* OTP Form */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                    Enter 6-digit verification code
                  </label>

                  {/* OTP Input Grid */}
                  <div className="flex justify-center space-x-2 sm:space-x-3 mb-4">
                    {otp.map((digit, index) => (
                      <OtpInput
                        key={index}
                        value={digit}
                        onChange={(value) => handleInputChange(index, value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onFocus={() => setError("")}
                        onPaste={index === 0 ? handlePaste : () => {}}
                        hasError={hasError}
                        disabled={isLoading}
                      />
                    ))}
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="flex items-center justify-center space-x-2 text-red-600 text-sm mt-3">
                      <AlertCircle className="w-4 h-4" />
                      <span>{error}</span>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="button"
                  onClick={() => handleSubmit()}
                  disabled={isLoading || !isOtpComplete}
                  className="w-full bg-primary hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      <span>Verify Email</span>
                    </>
                  )}
                </button>
              </div>

              {/* Resend OTP */}
              <div className="mt-6 text-center">
                <p className="text-gray-600 text-sm mb-3">
                  Didn't receive the code?
                </p>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || isResendLoading}
                  className="inline-flex items-center space-x-2 text-primary hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed font-medium text-sm transition-colors duration-200"
                >
                  {isResendLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : resendCooldown > 0 ? (
                    <span>Resend in {resendCooldown}s</span>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      <span>Resend Code</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SplitScreenLayout>
  );
};

export default VerifyEmailWithOtp;
